// WebSocket client — connects to relay, exchanges inputs

import type { PlayerInput } from "../engine/input.js";
import { encode, decode, type NetMessage } from "./protocol.js";
import type { ServerInfo } from "./server.js";

export type NetEventHandlers = {
  onWelcome: (playerId: number, playerCount: number, mapJson: string, server: ServerInfo) => void;
  onStart: (playerCount: number) => void;
  onInput: (tick: number, playerId: number, input: PlayerInput) => void;
  onPlayerJoined: (playerId: number, playerCount: number) => void;
  onPlayerLeft: (playerId: number, playerCount: number) => void;
  onServerFull?: () => void;
  onServerClosed?: (reason: string) => void;
  onError?: () => void;
};

export class NetClient {
  private ws: WebSocket | null = null;

  constructor(
    private url: string,
    private handlers: NetEventHandlers
  ) {}

  connect(): void {
    this.ws = new WebSocket(this.url);

    this.ws.onopen = () => {
      console.log("Connected to relay");
    };

    this.ws.onmessage = (event) => {
      const msg = decode(event.data as string);

      switch (msg.type) {
        case "welcome":
          this.handlers.onWelcome(msg.playerId, msg.playerCount, msg.mapJson, msg.server);
          break;
        case "start":
          this.handlers.onStart(msg.playerCount);
          break;
        case "input":
          this.handlers.onInput(msg.tick, msg.playerId, msg.input);
          break;
        case "player_joined":
          this.handlers.onPlayerJoined(msg.playerId, msg.playerCount);
          break;
        case "player_left":
          this.handlers.onPlayerLeft(msg.playerId, msg.playerCount);
          break;
        case "server_full":
          this.handlers.onServerFull?.();
          break;
        case "server_closed":
          this.handlers.onServerClosed?.(msg.reason);
          break;
      }
    };

    this.ws.onclose = () => {
      console.log("Disconnected from relay");
    };

    this.ws.onerror = () => {
      this.handlers.onError?.();
    };
  }

  sendInput(tick: number, playerId: number, input: PlayerInput): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(
        encode({ type: "input", tick, playerId, input })
      );
    }
  }
}
